import React, { useEffect, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import GoogleOnlyLoginPage from '@/pages/GoogleOnlyLoginPage'
import DemoLoginPage from '@/pages/DemoLoginPage'
import DashboardPage from '@/pages/DashboardPage'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { ParentHomePage } from '@/pages/dashboard/ParentHomePage'
import { KidHomePage } from '@/pages/dashboard/KidHomePage'
import { KidTilesPage } from '@/pages/dashboard/KidTilesPage'
import { ChoresPage } from '@/pages/dashboard/ChoresPage'
import { CalendarPage } from '@/pages/dashboard/CalendarPage'
import { PointsPage } from '@/pages/dashboard/PointsPage'
import { RewardsPage } from '@/pages/dashboard/RewardsPage'
import { TradingPage } from '@/pages/dashboard/TradingPage'
import { MealsPage } from '@/pages/dashboard/MealsPage'
import { RequestsPage } from '@/pages/dashboard/RequestsPage'
import { SettingsPage } from '@/pages/dashboard/SettingsPage'
import { AnalyticsPage } from '@/pages/dashboard/AnalyticsPage'
import { KidsPage } from '@/pages/dashboard/KidsPage'
import { SchoolPage } from '@/pages/dashboard/SchoolPage'
import { RecipesPage } from '@/pages/dashboard/RecipesPage'
import { ShoppingListPage } from '@/pages/dashboard/ShoppingListPage'
import { DefaultKioskDashboard } from '@/pages/DefaultKioskDashboard'
import { TVKioskDashboard } from '@/pages/TVKioskDashboard'
import { KioskPairApprovalPage } from '@/pages/KioskPairApprovalPage'
import { DevicePage } from '@/pages/device/DevicePage'
import { ApproveDevicePage } from '@/pages/device/ApproveDevicePage'
import { StepUpCodePage, ApproveStepUpPage } from '@/pages/device/StepUpPages'
import { FamilyHostGate } from '@/pages/device/FamilyHostGate'
import { SharedDeviceController } from '@/components/sharedDevice/SharedDeviceController'
import ParentSchoolManagement from '@/pages/ParentSchoolManagement'
import AdminDashboard from '@/pages/AdminDashboard'
import SessionsPage from '@/pages/SessionsPage'
import { PlatformAdminDashboard } from '@/pages/PlatformAdminDashboard'
import PlatformAdminLoginPage from '@/pages/PlatformAdminLoginPage'
import DemoSignupPage from '@/pages/DemoSignupPage'
import { useAuth } from '@/hooks/useAuth'
import ErrorBoundary from '@/components/ErrorBoundary'
import { FamilyProvider } from '@/contexts/FamilyContext'
import { FeaturesProvider } from '@/contexts/FeaturesContext'
import { initializeTimezone } from '@/utils/dateUtils'
import { apiClient } from '@/utils/api'
// Initialize global query client with cache settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

function App() {
  const hostname = window.location.hostname
  const isPlatformAdmin = hostname === 'admin.housepoints.ai'
  const location = useLocation()

  useEffect(() => {
    // Only initialize timezone for family sites, not platform admin
    if (!isPlatformAdmin) {
      initializeTimezone().catch(err => {
        console.warn('Failed to initialize timezone:', err)
      })
    }
    // Service worker disabled to fix cache issues
  }, [isPlatformAdmin])

  // Track page views on route changes for Umami analytics
  useEffect(() => {
    if (typeof window.umami !== 'undefined') {
      window.umami.track(props => ({ ...props, url: location.pathname + location.search }))
    }
  }, [location])

  // Platform admin subdomain gets dedicated admin dashboard
  if (isPlatformAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <div className="min-h-screen bg-gray-50">
            <Routes>
              <Route path="/login" element={<PlatformAdminLoginPage />} />
              <Route path="/" element={<PlatformProtectedRoute><PlatformAdminDashboard /></PlatformProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </ErrorBoundary>
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
          <Routes>
          <Route path="/login" element={<DemoOrFamilyLogin />} />
          <Route path="/demo/login" element={<DemoLoginPage />} />
          <Route path="/demo-signup" element={<DemoSignupPage />} />
          <Route path="/demo/signup" element={<DemoSignupPage />} />
          <Route path="/kiosk" element={<DefaultKioskView />} />
          <Route path="/kiosk-v2" element={<DefaultKioskView />} />
          <Route path="/kiosk/default" element={<DefaultKioskView />} />
          <Route path="/kiosk/tv" element={<TVKioskView />} />
          <Route path="/tv-kiosk" element={<TVKioskView />} />
          {/* One setup page for every browser device: /device shows a code, and the
              approving parent picks shared device or display only. */}
          <Route path="/kiosk/pair" element={<Navigate to="/device" replace />} />
          <Route path="/kiosk/activate" element={<Navigate to="/device" replace />} />
          <Route path="/pair" element={<ProtectedRoute><KioskPairApprovalPage /></ProtectedRoute>} />
          {/* Trusted family devices: the device pairs itself at /device; parents approve. */}
          <Route path="/device" element={<DevicePage />} />
          <Route path="/approve" element={<FamilyHostGate title="Approve a device"><ProtectedRoute><ApproveDevicePage /></ProtectedRoute></FamilyHostGate>} />
          <Route path="/step-up" element={<FamilyHostGate title="Approve a shared device"><ProtectedRoute><StepUpCodePage /></ProtectedRoute></FamilyHostGate>} />
          <Route path="/approve-step-up" element={<FamilyHostGate title="Parent approval request"><ProtectedRoute><ApproveStepUpPage /></ProtectedRoute></FamilyHostGate>} />
          <Route path="/settings" element={<SettingsRedirect />} />

          {/* Dashboard routes wrapped in DashboardLayout - redirects handled in layout based on user type */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            {/* Index renders nothing - layout handles redirect to /home or /chores based on user type */}
            <Route index element={null} />
            <Route path="home" element={<HomeSwitcher />} />
            <Route path="tiles" element={<KidTilesPage />} />
            <Route path="chores" element={<ChoresPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="schedule" element={<CalendarPage />} />
            <Route path="points" element={<PointsPage />} />
            <Route path="kids" element={<KidsPage />} />
            <Route path="rewards" element={<RewardsPage />} />
            <Route path="trading" element={<TradingPage />} />
            <Route path="meals" element={<MealsPage />} />
            <Route path="recipes" element={<Navigate to="/dashboard/meals?tab=recipes" replace />} />
            <Route path="shopping-list" element={<Navigate to="/dashboard/meals?tab=shopping" replace />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="school" element={<SchoolPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="sessions" element={<SessionsPage />} />
          </Route>

          {/* Legacy routes for backwards compatibility */}
          <Route path="/parent/school" element={<ProtectedRoute><ParentSchoolManagement /></ProtectedRoute>} />
          <Route path="/parent/sessions" element={<Navigate to="/dashboard/sessions" replace />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        </Routes>
        <SharedDeviceController />
        </div>
      </ErrorBoundary>
    </QueryClientProvider>
  )
}

// Route /login to demo login for demo.housepoints.ai, regular login for others
function DemoOrFamilyLogin() {
  const hostname = window.location.hostname

  // Demo subdomain gets demo login with account selection
  if (hostname.includes('demo.housepoints.ai') || hostname === 'localhost') {
    return <DemoLoginPage />
  }

  // All other families get Google-only login
  return <GoogleOnlyLoginPage />
}

// Home page switcher - shows parent or kid dashboard based on user type
function HomeSwitcher() {
  const { user } = useAuth()

  if (user?.is_parent) {
    return <ParentHomePage />
  }

  return <KidHomePage />
}

// Settings redirect for reauth
function SettingsRedirect() {
  useEffect(() => {
    // Clear any existing auth tokens
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    localStorage.removeItem('tokenExpiry')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('isLoggedIn')
    localStorage.removeItem('google_auth_status')
    
    // Use direct navigation to avoid React Router issues
    window.location.replace('/login')
  }, [])
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Redirecting to login...</h2>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    </div>
  )
}

// Default kiosk view wrapper - fetches config and renders the default dashboard
function DefaultKioskView() {
  const [kioskConfig, setKioskConfig] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Fetch kiosk config to determine which kiosk to show
    apiClient.get('/api/kiosk/config')
      .then((config: any) => {
        console.log('[DefaultKioskView] Kiosk config:', config);
        // The legacy Home Assistant kiosk (/kiosk-custom) was retired; the
        // Gamull HA wall display runs the LiveView kiosk (kiosk.housepoints.ai).
        // All React kiosk devices now render the default dashboard.
        setKioskConfig(config);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('[DefaultKioskView] Failed to fetch kiosk config:', err);
        // On error, just show default kiosk without config
        setKioskConfig({ features: {} });
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading kiosk configuration...</div>
      </div>
    );
  }

  return <DefaultKioskDashboard config={kioskConfig} />;
}

// TV kiosk view wrapper - optimized for smart TVs with D-pad navigation
function TVKioskView() {
  const [kioskConfig, setKioskConfig] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    apiClient.get('/api/kiosk/config')
      .then((config: any) => {
        console.log('[TVKioskView] Kiosk config:', config);
        setKioskConfig(config);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('[TVKioskView] Failed to fetch kiosk config:', err);
        setKioskConfig({ features: {} });
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-4xl">Loading TV kiosk...</div>
      </div>
    );
  }

  return <TVKioskDashboard config={kioskConfig} />;
}

// Protected route wrapper for family routes (includes FamilyProvider)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, token, loginFromToken } = useAuth()
  const navigate = useNavigate()

  // Check URL for OAuth token - use window.location directly for immediate access
  const urlParams = new URLSearchParams(window.location.search)
  const urlToken = urlParams.get('token') || urlParams.get('auth_token')
  const isGoogleLogin = urlParams.get('google_login') === 'true'

  // OAuth processing state - initialize based on URL token presence
  const [isProcessingOAuth, setIsProcessingOAuth] = React.useState(() => {
    if (urlToken) {
      // Clear stale data immediately
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      sessionStorage.removeItem('oauth_token_processed')
      sessionStorage.removeItem('current_processing_token')
      return true
    }
    return false
  })

  // Process OAuth token - this runs BEFORE any children render
  React.useEffect(() => {
    if (!urlToken || !isProcessingOAuth) return

    const processToken = async () => {
      console.log('[ProtectedRoute] Processing OAuth token directly')

      // Clear URL immediately
      window.history.replaceState({}, document.title, window.location.pathname)

      try {
        const result = await loginFromToken(urlToken)
        if (result.success) {
          console.log('[ProtectedRoute] OAuth success:', result.user.display_name)
          // Check if there's a saved return destination from before the OAuth redirect
          const savedReturnTo = sessionStorage.getItem('login_return_to')
          if (savedReturnTo) {
            sessionStorage.removeItem('login_return_to')
            navigate(savedReturnTo, { replace: true })
          }
          // Otherwise let the component re-render with authenticated state
        } else {
          console.error('[ProtectedRoute] OAuth failed:', result.error)
          navigate('/login', { replace: true })
        }
      } catch (error) {
        console.error('[ProtectedRoute] OAuth error:', error)
        navigate('/login', { replace: true })
      } finally {
        setIsProcessingOAuth(false)
      }
    }

    processToken()
  }, [urlToken, isProcessingOAuth, loginFromToken, navigate])

  // CRITICAL: Show loading screen while processing OAuth - blocks ALL children from rendering
  if (isProcessingOAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Signing you in...</p>
        </div>
      </div>
    )
  }

  // Also check if we have a stored token that hasn't been validated yet
  const storedToken = localStorage.getItem('auth_token')
  if (storedToken && !isAuthenticated) {
    // We have a token but auth state isn't set yet - allow rendering to process it
    // This prevents redirect loops during initialization
    return (
      <FamilyProvider>
        <FeaturesProvider>
          {children}
        </FeaturesProvider>
      </FamilyProvider>
    )
  }

  if (!isAuthenticated) {
    // Not authenticated - redirect to login, preserving intended destination
    // Keep the query (for example /approve?code=W-...) through sign-in.
    return <Navigate to="/login" replace state={{ returnTo: window.location.pathname + window.location.search }} />
  }

  // Authenticated - render protected content with FamilyProvider and FeaturesProvider
  return (
    <FamilyProvider>
      <FeaturesProvider>
        {children}
      </FeaturesProvider>
    </FamilyProvider>
  )
}

// Protected route wrapper for platform admin routes (NO FamilyProvider)
function PlatformProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, loginFromToken } = useAuth()
  const navigate = useNavigate()

  // Check URL for OAuth token
  const urlParams = new URLSearchParams(window.location.search)
  const urlToken = urlParams.get('token')
  const isGoogleLogin = urlParams.get('google_login') === 'true'

  // OAuth processing state
  const [isProcessingOAuth, setIsProcessingOAuth] = React.useState(() => {
    if (urlToken && isGoogleLogin) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      return true
    }
    return false
  })

  // Process OAuth token
  React.useEffect(() => {
    if (!urlToken || !isProcessingOAuth) return

    const processToken = async () => {
      window.history.replaceState({}, document.title, window.location.pathname)

      try {
        const result = await loginFromToken(urlToken)
        if (!result.success) {
          navigate('/login', { replace: true })
        }
      } catch (error) {
        navigate('/login', { replace: true })
      } finally {
        setIsProcessingOAuth(false)
      }
    }

    processToken()
  }, [urlToken, isProcessingOAuth, loginFromToken, navigate])

  // Show loading while processing OAuth
  if (isProcessingOAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Signing you in...</p>
        </div>
      </div>
    )
  }

  // Also check if we have a stored token that hasn't been validated yet
  const storedToken = localStorage.getItem('auth_token')
  if (storedToken && !isAuthenticated) {
    // We have a token but auth state isn't set yet - allow rendering to process it
    return <>{children}</>
  }

  if (!isAuthenticated) {
    // Not authenticated - redirect to login
    return <Navigate to="/login" replace />
  }

  // Authenticated - render protected content WITHOUT FamilyProvider
  // Platform admins don't need family context
  return <>{children}</>
}

export default App